<template>
  <div>
    <h3 class="mt-4 pt-2" v-if="isMatchedVOI">{{ $t('message.credit.inventoryMatching.matchedVOIHeading') }}</h3>
    <VehicleOfInterestCard
      v-if="matchedInventoryResults['vehicleOfInterest']"
      :class="{ 'mt-4': !isMatchedVOI }"
      :vehicleData="matchedInventoryResults['vehicleOfInterest']"
      data-test="voiCard"
    >
    </VehicleOfInterestCard>

    <template v-if="matchedInventoryResults.matchedVehicles && matchedInventoryResults.matchedVehicles.length > 0">
      <div class="mb-3 mt-4 pt-3 row form-row align-items-end">
        <div class="col-10">
          <h3 class="mb-0">{{ inventoryMatchedResultsHeading }}</h3>
        </div>
        <div class="text-right col-2">
          <p class="text-muted font-size-10 mt-0 mb-1">
            {{
              $tc('message.credit.vehicleFilters.numberOfResults', filteredResults.length, {
                count: filteredResults.length,
              })
            }}
          </p>
        </div>
      </div>

      <div class="horizontal-scroll-mobile-container">
        <mobialis-button
          v-for="(filter, index) in filters"
          :key="index"
          @click="openFilter(filter)"
          data-toggle="modal"
          data-target="#filterModal"
          class="filter-btn"
          width="w-auto"
          size="btn-sm"
          variant="secondary"
        >
          {{ $t(`message.credit.vehicleFilters.${filter}`) }}
          <img class="chevron ml-1" src="@/assets/fa/chevron-down.svg" alt="" />
        </mobialis-button>
      </div>

      <div v-if="selectedFilters.length > 0">
        <mobialis-button
          v-for="(filter, index) in selectedFilters"
          :key="`selectedFilters-${index}`"
          @click="removeFilter(filter)"
          class="selected-filter-btn"
          width="w-auto"
          size="btn-sm"
          variant="secondary"
        >
          <img :src="require('@/assets/times.svg')" :alt="$t('message.tooltip.close')" />
          {{ filter.label }}
        </mobialis-button>
        <mobialis-button @click="clearFilters()" class="clear-filters-btn" width="w-auto" size="btn-sm" variant="link">
          {{ $t('message.credit.vehicleFilters.clearAll') }}
        </mobialis-button>
      </div>

      <tool-tip theme="light" id="filterModal" :size="getModalSize(currentFilter)" ref="filterModal">
        <h3 class="d-sm-none text-center mt-0">{{ $t('message.credit.vehicleFilters.filters') }}</h3>

        <div
          class="filter-container mb-1"
          ref="filter-condition"
          :class="{ 'is-current-filter': currentFilter === 'condition' }"
        >
          <DropDownCardV2 :cardStyle="false" :defaultOpen="false" :mobileOnly="true" ref="dropdown-condition">
            <template v-slot:label>
              <div class="pb-3 pt-3 pt-sm-1 filter-container-label-container">
                <h4 class="m-0">{{ $t('message.credit.vehicleFilters.condition') }}</h4>
              </div>
            </template>
            <template v-slot:dropdown>
              <div class="pb-3 pb-sm-0">
                <div v-for="(option, index) in conditionOptions" :key="`condition-${index}`" class="checkbox">
                  <input
                    :id="`condition-${index}`"
                    type="checkbox"
                    v-model="conditions"
                    :value="option.value"
                    :disabled="option.disabled"
                  />
                  <label :for="`condition-${index}`">{{
                    $t(`message.credit.vehicleFilters.conditions.${option.value}`)
                  }}</label>
                </div>
              </div>
            </template>
          </DropDownCardV2>
        </div>

        <div
          class="filter-container mb-1"
          ref="filter-payment"
          :class="{ 'is-current-filter': currentFilter === 'payment' }"
        >
          <DropDownCardV2 :cardStyle="false" :defaultOpen="false" :mobileOnly="true" ref="dropdown-payment">
            <template v-slot:label>
              <div class="pb-3 pt-3 pt-sm-1">
                <h4 class="m-0">{{ $t('message.credit.vehicleFilters.monthlyPayment') }}</h4>
              </div>
            </template>
            <template v-slot:dropdown>
              <div class="pb-3 pb-sm-0">
                <Slider
                  id="paymentSlider"
                  :range-min="minPayment"
                  :range-max="maxPayment"
                  :initial-min="selectedMinPayment ? selectedMinPayment : minPayment"
                  :initial-max="selectedMaxPayment ? selectedMaxPayment : maxPayment"
                  @input="setPriceRange"
                  :value-type="'price'"
                >
                </Slider>
              </div>
            </template>
          </DropDownCardV2>
        </div>

        <div
          class="filter-container mb-1"
          ref="filter-mileage"
          :class="{ 'is-current-filter': currentFilter === 'mileage' }"
        >
          <DropDownCardV2 :cardStyle="false" :defaultOpen="false" :mobileOnly="true" ref="dropdown-mileage">
            <template v-slot:label>
              <div class="pb-3 pt-3 pt-sm-1">
                <h4 class="m-0">{{ $t('message.credit.vehicleFilters.mileage') }}</h4>
              </div>
            </template>
            <template v-slot:dropdown>
              <div class="pb-3 pb-sm-0">
                <Slider
                  id="mileageSlider"
                  :range-min="minMileage"
                  :range-max="maxMileage"
                  :initial-min="selectedMinMileage ? selectedMinMileage : minMileage"
                  :initial-max="selectedMaxMileage ? selectedMaxMileage : maxMileage"
                  @input="setMileageRange"
                  :value-type="'mileage'"
                >
                </Slider>
              </div>
            </template>
          </DropDownCardV2>
        </div>

        <div
          class="filter-container mb-1"
          ref="filter-bodyStyle"
          :class="{ 'is-current-filter': currentFilter === 'bodyStyle' }"
        >
          <DropDownCardV2 :cardStyle="false" :defaultOpen="false" :mobileOnly="true" ref="dropdown-bodyStyle">
            <template v-slot:label>
              <div class="pb-3 pt-3 pt-sm-1">
                <h4 class="m-0">{{ $t('message.credit.vehicleFilters.bodyStyle') }}</h4>
              </div>
            </template>
            <template v-slot:dropdown>
              <div class="pb-3 pb-sm-0">
                <div v-for="(option, index) in bodyStyleOptions" :key="`bodyStyle-${index}`" class="checkbox">
                  <input
                    :id="`bodyStyle-${index}`"
                    type="checkbox"
                    v-model="bodyStyles"
                    :value="option.value"
                    :disabled="option.disabled"
                  />
                  <label :for="`bodyStyle-${index}`">{{
                    $t(`message.credit.vehicleFilters.categories.${option.value}`)
                  }}</label>
                </div>
              </div>
            </template>
          </DropDownCardV2>
        </div>

        <div class="filter-container mb-1" ref="filter-year" :class="{ 'is-current-filter': currentFilter === 'year' }">
          <DropDownCardV2 :cardStyle="false" :defaultOpen="false" :mobileOnly="true" ref="dropdown-year">
            <template v-slot:label>
              <div class="pb-3 pt-3 pt-sm-1">
                <h4 class="m-0">{{ $t('message.credit.vehicleFilters.year') }}</h4>
              </div>
            </template>
            <template v-slot:dropdown>
              <div class="pb-3 pb-sm-0">
                <div v-for="(option, index) in yearOptions" :key="`year-${index}`" class="checkbox">
                  <input
                    :id="`year-${index}`"
                    type="checkbox"
                    v-model="years"
                    :value="option.value"
                    :disabled="option.disabled"
                  />
                  <label :for="`year-${index}`">{{ option.value }}</label>
                </div>
              </div>
            </template>
          </DropDownCardV2>
        </div>

        <div
          class="filter-container mb-1"
          ref="filter-makeAndModel"
          :class="{ 'is-current-filter': currentFilter === 'makeAndModel' }"
        >
          <DropDownCardV2 :cardStyle="false" :defaultOpen="false" :mobileOnly="true" ref="dropdown-makeAndModel">
            <template v-slot:label>
              <div class="pb-3 pt-3 pt-sm-1">
                <h4 class="m-0">{{ $t('message.credit.vehicleFilters.makeAndModel') }}</h4>
              </div>
            </template>
            <template v-slot:dropdown>
              <div class="pb-3 pb-sm-0">
                <div :class="`filters-${getModalSizeByCount(makeAndModelCount)}`">
                  <div v-for="(option, index) in makeAndModelOptions" :key="`makes-${index}`" class="checkbox">
                    <input
                      :id="`makes-${index}`"
                      type="checkbox"
                      v-model="makes"
                      :value="option.make"
                      :disabled="option.disabled"
                    />
                    <label :for="`makes-${index}`"> {{ option.make }} </label>
                    <div class="nested-checkboxes">
                      <div v-for="(option2, index2) in option.models" :key="`models-${index2}`" class="checkbox">
                        <input
                          :id="`models-${index}-${index2}`"
                          type="checkbox"
                          v-model="models"
                          :value="option2.value"
                          :disabled="option2.disabled"
                        />
                        <label :for="`models-${index}-${index2}`">{{ option2.value }}</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </DropDownCardV2>
        </div>

        <div class="filter-container mb-1" ref="filter-trim" :class="{ 'is-current-filter': currentFilter === 'trim' }">
          <DropDownCardV2 :cardStyle="false" :defaultOpen="false" :mobileOnly="true" ref="dropdown-trim">
            <template v-slot:label>
              <div class="pb-3 pt-3 pt-sm-1">
                <h4 class="m-0">{{ $t('message.credit.vehicleFilters.trim') }}</h4>
              </div>
            </template>
            <template v-slot:dropdown>
              <div class="pb-3 pb-sm-0">
                <div v-if="displayTrimFilters" :class="`filters-${getModalSize('trim')}`">
                  <div v-for="(model, index) in trimsByMakeAndModel" :key="index" class="mb-2">
                    <h5 class="text-muted">{{ model.make }} {{ model.model }}</h5>
                    <div v-for="(option, index2) in model.trims" :key="`trim-${index2}`" class="checkbox">
                      <input
                        :id="`trim-${index}-${index2}`"
                        type="checkbox"
                        v-model="trims"
                        :value="`${model.make}__${model.model}__${option}`"
                      />
                      <label :for="`trim-${index}-${index2}`">{{ option }}</label>
                    </div>
                  </div>
                </div>
                <div v-else class="mb-3">
                  {{ $t('message.credit.vehicleFilters.pleaseSelectMakeAndModel') }}
                </div>
              </div>
            </template>
          </DropDownCardV2>
        </div>

        <div class="mobile-filter-buttons d-sm-none">
          <mobialis-button data-dismiss="modal">
            {{
              $tc('message.credit.vehicleFilters.viewVehicles', filteredResults.length, {
                number: filteredResults.length,
              })
            }}
          </mobialis-button>
          <mobialis-button data-dismiss="modal" @click="clearFilters()" variant="link">
            {{ $t('message.credit.vehicleFilters.clearAll') }}
          </mobialis-button>
        </div>
      </tool-tip>

      <div v-if="filteredResults.length === 0" class="text-center p-5">
        <span class="primary-fill-svg error-svg">
          <inline-svg :src="require('@/assets/error-icon-sm.svg')" alt="" />
        </span>
        <h4>{{ $t('message.credit.vehicleFilters.noResultsHeading') }}</h4>
        <p>{{ $t('message.credit.vehicleFilters.noResultsMsg') }}</p>
      </div>
      <div v-else class="row form-row mt-3">
        <div class="col-sm-4 mb-2" v-for="(vehicleData, index) in filteredResults" :key="index" data-test="vehicleCard">
          <VehicleCard :vehicleData="vehicleData" :fadeInRightAway="index < 9"></VehicleCard>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapGetters, mapState } from 'vuex';
import VehicleOfInterestCard from '@/components/credit/VehicleOfInterestCard.vue';
import VehicleCard from '@/components/credit/VehicleCard.vue';
import ToolTip from '@/components/ToolTip.vue';
import Slider from '@/components/shared/Slider.vue';
import DropDownCardV2 from '@/components/DropDownCardV2.vue';

export default {
  name: 'MatchedInventoryResults',
  components: {
    VehicleOfInterestCard,
    VehicleCard,
    ToolTip,
    Slider,
    DropDownCardV2,
  },
  data() {
    return {
      conditions: [],
      bodyStyles: [],
      years: [],
      makes: [],
      models: [],
      trims: [],
      selectedMinPayment: null,
      selectedMaxPayment: null,
      selectedMinMileage: null,
      selectedMaxMileage: null,
      filters: ['condition', 'payment', 'mileage', 'bodyStyle', 'year', 'makeAndModel', 'trim'],
      currentFilter: null,
    };
  },
  computed: {
    ...mapState('credit', ['matchedInventoryResults']),
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
    }),
    filteredResults() {
      return this.getResults(true, true, true, true, true, true, true, true);
    },
    isMatchedVOI() {
      return this.matchedInventoryResults.vehicleOfInterest && this.matchedInventoryResults.vehicleOfInterest.isMatched;
    },
    isVOI() {
      return !!this.matchedInventoryResults.vehicleOfInterest;
    },
    inventoryMatchedResultsHeading() {
      if (this.isMatchedVOI) {
        return this.$tc(
          'message.credit.inventoryMatching.matchedResultsHeading',
          this.matchedInventoryResults.numMatchedVehicle,
          { number: this.matchedInventoryResults.numMatchedVehicle }
        );
      }
      if (!this.isVOI) {
        return this.$tc(
          'message.credit.inventoryMatching.matchedResultsHeadingNoVOI',
          this.matchedInventoryResults.numMatchedVehicle,
          { number: this.matchedInventoryResults.numMatchedVehicle }
        );
      }
      return this.$tc(
        'message.credit.inventoryMatching.matchedResultsHeadingNoVOIMatch',
        this.matchedInventoryResults.numMatchedVehicle,
        { number: this.matchedInventoryResults.numMatchedVehicle }
      );
    },
    conditionOptions() {
      const allVehicles = cloneDeep(this.matchedInventoryResults.matchedVehicles);
      const allOptions = [...new Set(allVehicles.map(item => item.vehicle.status))].filter(Boolean).sort();

      const filteredRes = this.getResults(false, true, true, true, true, true, true, true);
      const filteredOptions = [...new Set(filteredRes.map(item => item.vehicle.status))].filter(Boolean).sort();

      return allOptions.map(option => ({
        value: option,
        disabled: !filteredOptions.includes(option),
      }));
    },
    paymentOptions() {
      const res = cloneDeep(this.matchedInventoryResults.matchedVehicles);
      return [...new Set(res.map(item => item.paymentMonthly))];
    },
    minPayment() {
      return Math.round(Math.min(...this.paymentOptions));
    },
    maxPayment() {
      return Math.round(Math.max(...this.paymentOptions));
    },
    mileageOptions() {
      const res = cloneDeep(this.matchedInventoryResults.matchedVehicles);
      return [...new Set(res.map(item => item.vehicle.kilometers))];
    },
    minMileage() {
      return Math.round(Math.min(...this.mileageOptions));
    },
    maxMileage() {
      return Math.round(Math.max(...this.mileageOptions));
    },
    bodyStyleOptions() {
      const allVehicles = cloneDeep(this.matchedInventoryResults.matchedVehicles);
      const allOptions = [...new Set(allVehicles.map(item => item.vehicle.category))].filter(Boolean).sort();

      const filteredRes = this.getResults(true, false, true, true, true, true, true, true);
      const filteredOptions = [...new Set(filteredRes.map(item => item.vehicle.category))].filter(Boolean).sort();

      return allOptions.map(option => ({
        value: option,
        disabled: !filteredOptions.includes(option),
      }));
    },
    yearOptions() {
      const allVehicles = cloneDeep(this.matchedInventoryResults.matchedVehicles);
      const allOptions = [...new Set(allVehicles.map(item => item.vehicle.year))].filter(Boolean).sort();

      const filteredRes = this.getResults(true, true, false, true, true, true, true, true);
      const filteredOptions = [...new Set(filteredRes.map(item => item.vehicle.year))].filter(Boolean).sort();

      return allOptions.map(option => ({
        value: option,
        disabled: !filteredOptions.includes(option),
      }));
    },
    trimOptions() {
      const res = this.getResults(true, true, true, true, true, false, true, true);
      return [...new Set(res.map(item => item.vehicle.trim))].filter(Boolean).sort();
    },
    makeAndModelOptions() {
      const allVehicles = cloneDeep(this.matchedInventoryResults.matchedVehicles);
      const allOptions = [...new Set(allVehicles.map(item => item.vehicle.make))].filter(Boolean).sort();

      const filteredRes = this.getResults(true, true, true, false, false, false, true, true);
      const filteredOptions = [...new Set(filteredRes.map(item => item.vehicle.model))].filter(Boolean).sort();

      const res = [];
      allOptions.forEach(make => {
        const models = allVehicles.filter(item => item.vehicle.make === make).map(item => item.vehicle.model);
        const modelsForMake = [...new Set(models)].filter(Boolean).sort();
        const isDisabledModels = modelsForMake.map(model => ({
          value: model,
          disabled: !filteredOptions.includes(model),
        }));
        res.push({ make, models: isDisabledModels, disabled: this.isEveryModelDisabled(isDisabledModels) });
      });
      return res;
    },
    trimsByMakeAndModel() {
      const res = [];
      const vehicles = this.getResults(true, true, true, true, true, false, true, true);
      const groupByMakeAndModel = vehicles.reduce((acc, item) => {
        const group = `${item.vehicle.make}__${item.vehicle.model}`;
        if (!acc[group]) {
          acc[group] = [];
        }
        acc[group].push(item);
        return acc;
      }, {});
      // eslint-disable-next-line no-unused-vars
      Object.entries(groupByMakeAndModel).forEach(([key, value]) => {
        res.push({
          name: key,
          make: key.split('__')[0],
          model: key.split('__')[1],
          trims: [...new Set(groupByMakeAndModel[key].map(item => item.vehicle.trim))].filter(Boolean).sort(),
        });
      });
      return res.sort((a, b) => a.name.localeCompare(b.name));
    },
    displayTrimFilters() {
      return this.makes.length > 0 && this.models.length > 0;
    },
    makeAndModelCount() {
      const allVehicles = cloneDeep(this.matchedInventoryResults.matchedVehicles);
      const allOptionsMake = [...new Set(allVehicles.map(item => item.vehicle.make))].filter(Boolean).sort();
      const allOptionsModel = [...new Set(allVehicles.map(item => item.vehicle.model))].filter(Boolean).sort();
      return allOptionsMake.length + allOptionsModel.length;
    },
    trimsFilterCount() {
      // number of unique trims + number of unique make and models
      return this.trimOptions.length + this.trimsByMakeAndModel.length;
    },
    selectedFilters() {
      const res = [];
      this.conditions.forEach(filter => {
        res.push({
          type: 'conditions',
          value: filter,
          label: this.$t(`message.credit.vehicleFilters.conditions.${filter}`),
        });
      });
      if (
        (this.selectedMinPayment && this.selectedMinPayment !== this.minPayment) ||
        (this.selectedMaxPayment && this.selectedMaxPayment !== this.maxPayment)
      ) {
        res.push({
          type: 'payment',
          label: `${this.$t('message.credit.vehicleFilters.payment')}: ${this.$n(
            this.selectedMinPayment,
            'currencyDollars',
            this.languageCode
          )} - ${this.$n(this.selectedMaxPayment, 'currencyDollars', this.languageCode)}`,
        });
      }
      if (
        (this.selectedMinMileage && this.selectedMinMileage !== this.minMileage) ||
        (this.selectedMaxMileage && this.selectedMaxMileage !== this.maxMileage)
      ) {
        res.push({
          type: 'mileage',
          label: `${this.$t('message.credit.vehicleFilters.mileage')}: ${this.$n(
            this.selectedMinMileage,
            'number',
            this.languageCode
          )} ${this.$t('message.credit.inventoryMatching.km')} - ${this.$n(
            this.selectedMaxMileage,
            'number',
            this.languageCode
          )} ${this.$t('message.credit.inventoryMatching.km')}`,
        });
      }
      this.bodyStyles.forEach(filter => {
        res.push({
          type: 'bodyStyles',
          value: filter,
          label: this.$t(`message.credit.vehicleFilters.categories.${filter}`),
        });
      });
      const filters = ['years', 'makes', 'models'];
      filters.forEach(filter => {
        if (this[filter].length > 0) {
          this[filter].forEach(value => {
            res.push({ type: filter, value, label: value });
          });
        }
      });
      this.trims.forEach(filter => {
        res.push({
          type: 'trims',
          value: filter,
          label: `${filter.split('__')[1]} ${filter.split('__')[2]}`,
        });
      });
      return res;
    },
  },
  watch: {
    makes(newValue, oldValue) {
      if (newValue.length > oldValue.length) {
        // If we add a make select all the children models
        const addedMake = newValue.filter(item => !oldValue.includes(item))[0];
        const childModels = this.makeAndModelOptions.filter(item => item.make === addedMake)[0].models;
        const childModelsFilter = childModels.map(item => item.value);
        this.models = this.models.concat(childModelsFilter);
      } else if (newValue.length < oldValue.length) {
        // If a make is removed, we need to remove children models
        const removedMake = oldValue.filter(item => !newValue.includes(item))[0];
        const childModels = this.makeAndModelOptions.filter(item => item.make === removedMake)[0].models;
        const childModelsFilter = childModels.map(item => item.value);
        this.models = this.models.filter(item => !childModelsFilter.includes(item));
      }
    },
    models(newValue, oldValue) {
      if (newValue.length > oldValue.length) {
        // If we add a model without the parent make already selected, select the parent
        const addedModel = newValue.filter(item => !oldValue.includes(item))[0];
        const parentMake = this.makeAndModelOptions.filter(item =>
          item.models.some(item2 => item2.value === addedModel)
        )[0].make;
        if (!this.makes.includes(parentMake)) {
          this.makes.push(parentMake);
        }
      } else if (newValue.length < oldValue.length) {
        // If a model is removed and it's the only child model selected, remove the parent make
        const removedModel = oldValue.filter(item => !newValue.includes(item))[0];
        const makeObject = this.makeAndModelOptions.filter(item =>
          item.models.some(item2 => item2.value === removedModel)
        )[0];
        const siblingModels = makeObject.models.map(item => item.value);
        if (siblingModels.filter(value => this.models.includes(value)).length === 0) {
          const parentMake = makeObject.make;
          this.makes = this.makes.filter(item => item !== parentMake);
        }
        // If a model is removed, we need to check if we need to remove trims
        if (this.models.length === 0) {
          this.trims = [];
        } else {
          this.trims = this.trims.filter(item => !item.includes(`__${removedModel}__`));
        }
      }
    },
  },
  methods: {
    getResults(condition, bodyStyle, year, make, model, trim, payment, mileage) {
      let res = cloneDeep(this.matchedInventoryResults.matchedVehicles).filter(
        item =>
          (condition && this.conditions.length > 0 ? this.conditions.includes(item.vehicle.status) : true) &&
          (bodyStyle && this.bodyStyles.length > 0 ? this.bodyStyles.includes(item.vehicle.category) : true) &&
          (payment && this.selectedMinPayment ? Math.round(item.paymentMonthly) >= this.selectedMinPayment : true) &&
          (payment && this.selectedMaxPayment ? Math.round(item.paymentMonthly) <= this.selectedMaxPayment : true) &&
          (mileage && this.selectedMinMileage ? item.vehicle.kilometers >= this.selectedMinMileage : true) &&
          (mileage && this.selectedMaxMileage ? item.vehicle.kilometers <= this.selectedMaxMileage : true) &&
          (year && this.years.length > 0 ? this.years.includes(item.vehicle.year) : true) &&
          (make && this.makes.length > 0 ? this.makes.includes(item.vehicle.make) : true) &&
          (model && this.models.length > 0 ? this.models.includes(item.vehicle.model) : true)
      );

      if (trim && this.trims.length > 0) {
        res = this.filterResultsByTrim(res);
      }

      return res;
    },
    filterResultsByTrim(res) {
      // if we have trims set make sure they are filtered in our results
      // some models might have the same trim as other models, so we need to make sure the selected trim matches the correct model
      // ie Jeep Wrangler Rubicon vs Jeep Gladiator Rubicon
      const trimsByMakeAndModel = this.trims.reduce((acc, item) => {
        const [trimMake, trimModel, trimName] = item.split('__');
        const trimKey = `${trimMake}__${trimModel}`;
        acc[trimKey] = acc[trimKey] || [];
        acc[trimKey].push(trimName);
        return acc;
      }, {});
      return res.filter(vehicle => {
        const vehicleKey = `${vehicle.vehicle.make}__${vehicle.vehicle.model}`;
        return trimsByMakeAndModel[vehicleKey] && trimsByMakeAndModel[vehicleKey].includes(vehicle.vehicle.trim);
      });
    },
    setPriceRange(val) {
      this.selectedMinPayment = val.selectedMinValue;
      this.selectedMaxPayment = val.selectedMaxValue;
    },
    setMileageRange(val) {
      this.selectedMinMileage = val.selectedMinValue;
      this.selectedMaxMileage = val.selectedMaxValue;
    },
    removeFilter(filter) {
      if (filter.type === 'mileage') {
        this.selectedMinMileage = null;
        this.selectedMaxMileage = null;
      } else if (filter.type === 'payment') {
        this.selectedMinPayment = null;
        this.selectedMaxPayment = null;
      } else {
        this[filter.type] = this[filter.type].filter(value => value !== filter.value);
      }
    },
    clearFilters() {
      this.conditions = [];
      this.bodyStyles = [];
      this.years = [];
      this.makes = [];
      this.models = [];
      this.trims = [];
      this.selectedMinPayment = null;
      this.selectedMaxPayment = null;
      this.selectedMinMileage = null;
      this.selectedMaxMileage = null;
    },
    getModalSizeByCount(count) {
      if (count > 20) {
        return 'lg';
      }
      if (count > 8) {
        return 'md';
      }
      return 'sm';
    },
    getModalSize(filter) {
      if (this.isMobileScreenSize) {
        return 'lg';
      }
      if (['condition', 'bodyStyle', 'year'].includes(filter)) {
        return 'sm';
      }
      if (['payment', 'mileage'].includes(filter)) {
        return 'md';
      }
      if (filter === 'trim') {
        return this.displayTrimFilters ? this.getModalSizeByCount(this.trimsFilterCount) : 'md';
      }
      if (filter === 'makeAndModel') {
        return this.getModalSizeByCount(this.makeAndModelCount);
      }
      return 'sm';
    },
    openFilter(filter) {
      this.currentFilter = filter;
      // close all other filters
      this.filters.forEach(item => {
        if (item !== filter) {
          this.$refs[`dropdown-${item}`].setDropdownIsOpen(false);
        }
      });
      // open this filter
      this.$refs[`dropdown-${filter}`].setDropdownIsOpen(true);
      // wait for animation to finish
      setTimeout(() => {
        const filterSection = this.$refs[`filter-${filter}`];
        this.$refs.filterModal.$el.scrollTop = filterSection.offsetTop;
      }, 200);
    },
    isEveryModelDisabled(models) {
      return models.length > 0 && models.every(model => model.disabled);
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';

.nested-checkboxes {
  margin-bottom: 10px;
  margin-left: 30px;
}

.checkbox {
  label {
    position: relative;
    padding-left: 30px;
    padding-top: 1px;
  }
  input[type='checkbox']:enabled + label {
    cursor: pointer;
    &:hover {
      &:before {
        border: 1px solid #909fab;
      }
    }
  }
  label:before,
  label:after {
    position: absolute;
  }
  label::before {
    content: '';
    display: inline-block;
    height: 20px;
    width: 20px;
    border: 1px solid #c5cdd6;
    border-radius: 4px;
    top: 3px;
    left: 0;
  }
  label::after {
    content: '';
    display: inline-block;
    height: 6px;
    width: 11px;
    border-left: 2px solid #ffffff;
    border-bottom: 2px solid #ffffff;
    transform: rotate(-45deg);
    left: 4px;
    top: 8px;
  }
  input[type='checkbox'] {
    display: none;
  }
  input[type='checkbox'] + label::after {
    content: none;
  }
  input[type='checkbox']:checked + label::after {
    content: '';
  }
  input[type='checkbox']:checked:disabled + label::after {
    border-left: 2px solid #788796;
    border-bottom: 2px solid #788796;
  }
  input[type='checkbox']:checked:disabled + label:before {
    background: #f7f9fb;
    border: 1px solid #ecf1f5;
  }
  input[type='checkbox']:checked + label:before {
    background: $primary;
  }
  input[type='checkbox']:checked:focus + label:before {
    border: 2px solid $primary-bright;
  }
  input[type='checkbox']:focus + label::before {
    border: 1px solid $primary;
  }
  input[type='checkbox']:disabled + label {
    opacity: 0.7;
    color: #788796;
  }
}

@media (min-width: 576px) {
  .filters-lg {
    column-count: 3;
    column-gap: 10px;
  }
  .filters-md {
    column-count: 2;
    column-gap: 10px;
  }
  .filters-lg > *,
  .filters-md > * {
    page-break-inside: avoid;
    break-inside: avoid-column;
    display: table;
  }
}

.btn.filter-btn::v-deep {
  border-radius: 20px;
  padding: 6px 12px;
  display: inline-block;
  margin-bottom: 10px;
  img.chevron {
    display: inline-block;
    height: 12px;
  }
  + .filter-btn {
    margin-left: 3px;
  }
}

.btn.selected-filter-btn::v-deep {
  color: #4b5863;
  padding: 4px 6px;
  margin: 7px 7px 0 0;
  img {
    margin: 0 2px 2px 0;
    width: 9px;
    opacity: 0.8;
  }
}

.btn.clear-filters-btn::v-deep {
  padding: 4px 6px;
  margin: 7px 7px 0 0;
}

#filterModal::v-deep {
  .mobile-filter-buttons {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 20px 20px 10px;
    background-color: #fff;
  }

  @media (min-width: 576px) {
    .filter-container {
      display: none;

      &.is-current-filter {
        display: block;
      }
    }
  }

  @media (max-width: 576px) {
    .filter-container {
      border-bottom: 1px solid #dae2ea;
    }
    .modal-body {
      padding-bottom: 110px;
    }
  }
}

@media (max-width: 576px) {
  .horizontal-scroll-mobile-container {
    padding: 10px 0;
    width: calc(100vw - 30px);
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;
    overflow-y: hidden;
    white-space: nowrap;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
}
</style>
